import React, { useState } from 'react';
import useSWR from 'swr';
import { 
  UserPlus, 
  Users, 
  Percent, 
  ExternalLink, 
  MoreVertical,
  Check,
  Clock,
  Mail,
  Trash2,
  RefreshCw,
  Tag,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ShieldCheck,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { toast } from 'sonner';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import apiV2 from '../utils/apiV2';

const fetcher = (url) => apiV2.get(url).then(res => res.data);

const PartnersTab = ({ organizationId }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [partnerToDelete, setPartnerToDelete] = useState(null);
  const urlParamsProcessed = React.useRef(false);
  
  // Fetch partners
  const { data: partnersData, isLoading, mutate } = useSWR(
    organizationId ? `/organizations/${organizationId}/partners` : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  
  // Fetch recent payouts
  const { data: payoutsData } = useSWR(
    organizationId ? `/organizations/${organizationId}/partners/payouts/recent?limit=10` : null,
    fetcher,
    { revalidateOnFocus: true }
  );
  
  const partners = partnersData?.partners || [];
  const payouts = payoutsData?.payouts || [];
  
  // Handle URL params for partner actions (e.g., from email links)
  React.useEffect(() => {
    // Only proceed if organizationId is available and we haven't processed yet
    if (!organizationId || urlParamsProcessed.current) return;
    
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const partnerId = params.get('partner_id');
    const connected = params.get('connected');
    
    if (connected === 'true' && partnerId) {
      urlParamsProcessed.current = true;
      // Partner just completed Stripe Connect - refresh their status
      toast.success('Stripe account connected! Checking status...');
      handleCheckStatus({ id: partnerId });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=partners');
    } else if (action === 'connect' && partnerId && partners.length > 0) {
      urlParamsProcessed.current = true;
      // Find the partner and trigger connect
      const partner = partners.find(p => p.id === partnerId);
      if (partner && !partner.stripe_onboarding_complete) {
        handleCreateOnboardingLink(partner);
      }
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=partners');
    }
  }, [organizationId, partners]);
  
  const handleCreateOnboardingLink = async (partner) => {
    setActionLoading(partner.id);
    try {
      const response = await apiV2.post(
        `/organizations/${organizationId}/partners/${partner.id}/create-onboarding-link`
      );
      
      if (response.data.success) {
        // Open Stripe onboarding in new tab
        window.open(response.data.onboarding_url, '_blank');
        toast.success('Stripe onboarding opened in new tab');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create onboarding link');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleCheckStatus = async (partner) => {
    setActionLoading(partner.id);
    try {
      const response = await apiV2.post(
        `/organizations/${organizationId}/partners/${partner.id}/check-stripe-status`
      );
      
      if (response.data.success) {
        mutate();
        toast.success(`Status: ${response.data.status}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check status');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleResendInvite = async (partner) => {
    setActionLoading(partner.id);
    try {
      await apiV2.post(
        `/organizations/${organizationId}/partners/${partner.id}/resend-invite`
      );
      toast.success(`Invite resent to ${partner.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleRemovePartner = async () => {
    if (!partnerToDelete) return;
    
    setActionLoading(partnerToDelete.id);
    try {
      await apiV2.delete(`/organizations/${organizationId}/partners/${partnerToDelete.id}`);
      mutate();
      toast.success('Partner removed');
      setPartnerToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove partner');
    } finally {
      setActionLoading(null);
    }
  };
  
  if (isLoading) {
    return <PageLoader />;
  }
  
  return (
    <TabPageLayout
      title="Partners"
      subtitle="Revenue sharing with partners and affiliates"
      actions={
        <Button 
          onClick={() => setShowAddModal(true)} 
          className="gap-2 bg-blue-600 hover:bg-blue-700 text-white h-10"
          data-testid="add-partner-btn"
        >
          <UserPlus className="w-4 h-4" />
          Add
        </Button>
      }
    >
      {/* Partners List */}
      {partners.length === 0 ? (
        <EmptyState
          icon={<UserPlus />}
          title="No partners yet"
          description="Add revenue partners or affiliates to automatically share earnings"
          action={
            <Button 
              onClick={() => setShowAddModal(true)} 
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white h-10"
              data-testid="add-partner-empty-btn"
            >
              <UserPlus className="w-4 h-4" />
              Add Partner
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4">
          {partners.map((partner) => (
            <PartnerCard
              key={partner.id}
              partner={partner}
              organizationId={organizationId}
              loading={actionLoading === partner.id}
              onCreateOnboardingLink={() => handleCreateOnboardingLink(partner)}
              onCheckStatus={() => handleCheckStatus(partner)}
              onResendInvite={() => handleResendInvite(partner)}
              onRemove={() => setPartnerToDelete(partner)}
              onEdit={() => setSelectedPartner(partner)}
            />
          ))}
        </div>
      )}
      
      {/* Recent Payouts */}
      {payouts.length > 0 && (
        <div className="mt-8 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Payouts</h3>
          
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Booking</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payouts.map((payout) => (
                    <tr key={payout.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(payout.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {payout.partner_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                        #{payout.booking_confirmation_code}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                        ${payout.amount.toFixed(2)}
                        <span className="text-xs text-gray-500 ml-1">({payout.percentage_applied}%)</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={payout.status === 'completed' ? 'default' : 'secondary'}>
                          {payout.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Partner Modal */}
      <AddPartnerModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        organizationId={organizationId}
        onSuccess={() => {
          mutate();
          setShowAddModal(false);
        }}
      />
      
      {/* Edit Partner Modal */}
      {selectedPartner && (
        <EditPartnerModal
          open={!!selectedPartner}
          onClose={() => setSelectedPartner(null)}
          partner={selectedPartner}
          organizationId={organizationId}
          onSuccess={() => {
            mutate();
            setSelectedPartner(null);
          }}
        />
      )}
      
      {/* Delete Partner Confirmation Modal */}
      <Dialog open={!!partnerToDelete} onOpenChange={() => setPartnerToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Remove Partner
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{partnerToDelete?.name}</strong> as a partner?
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
            <p className="text-sm text-red-800">
              <strong>This action will:</strong>
            </p>
            <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
              <li>Stop all future revenue sharing with this partner</li>
              <li>Remove their access to earnings from your services</li>
              <li>Disconnect their Stripe account from your organization</li>
            </ul>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setPartnerToDelete(null)}
              disabled={actionLoading === partnerToDelete?.id}
              className="h-10"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRemovePartner}
              disabled={actionLoading === partnerToDelete?.id}
              className="h-10"
            >
              {actionLoading === partnerToDelete?.id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Remove Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};


// Partner Card Component
const PartnerCard = ({ 
  partner, 
  organizationId,
  loading,
  onCreateOnboardingLink,
  onCheckStatus,
  onResendInvite,
  onRemove,
  onEdit
}) => {
  const [portalLoading, setPortalLoading] = useState(false);
  const partnerType = partner.partnerType || partner.partner_type;
  const percentage = partner.defaultSplitPercentage || partner.percentage;
  const referralCode = partner.referral_code;
  const stripeEnabled = partner.stripeConnectEnabled || partner.stripe_onboarding_complete;
  const stripeAccountId = partner.stripeConnectAccountId || partner.stripe_account_id;
  const applyToAll = partner.apply_to_all_services !== false;
  const serviceIds = partner.assignedServices || partner.service_ids || [];
  
  const isConnected = partner.status === 'connected' || (partner.status === 'active' && stripeEnabled);
  const isPending = partner.status === 'pending';
  
  const handleViewPortal = async () => {
    try {
      setPortalLoading(true);
      const res = await apiV2.get(`/organizations/${organizationId}/partners/${partner.id}/portal-link`);
      if (res.data?.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (err) {
      toast.error('Failed to generate portal link');
    } finally {
      setPortalLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-xl p-4 sm:p-5 shadow-[0_8px_24px_rgba(0,0,0,0.04)]" data-testid={`partner-card-${partner.id}`}>
      <div className="flex items-start justify-between gap-4">
        {/* Left: Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100">
              <Users className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-gray-900">{partner.name}</h4>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {partnerType === 'affiliate' ? 'Affiliate' : 'Partner'}
                </span>
              </div>
              <p className="text-sm text-gray-500">{partner.email}</p>
            </div>
          </div>
          
          {/* Details row */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 text-sm">
            <span className="text-gray-700 font-medium bg-gray-50 px-2 py-0.5 rounded border border-gray-200">
              {percentage}%
            </span>
            
            {referralCode && (
              <span className="text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200 font-mono text-xs">
                {referralCode}
              </span>
            )}
            
            <span className="text-gray-500 text-xs">
              {applyToAll ? 'All Services' : `${serviceIds.length || 0} Services`}
            </span>
          </div>
          
          {/* View Portal link */}
          <div className="mt-3">
            <button 
              onClick={handleViewPortal}
              disabled={portalLoading}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center gap-1.5 hover:underline disabled:opacity-50"
              data-testid={`partner-portal-link-${partner.id}`}
            >
              {portalLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ExternalLink className="w-3.5 h-3.5" />
              )}
              View Partner Portal
            </button>
          </div>
        </div>
        
        {/* Right: Status & Actions */}
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Badge className="bg-green-100 text-green-700 gap-1 hover:bg-green-100 shadow-none border-0">
              <Check className="w-3 h-3" />
              Connected
            </Badge>
          ) : isPending ? (
            <Badge variant="secondary" className="gap-1">
              <Clock className="w-3 h-3" />
              Pending
            </Badge>
          ) : (
            <Badge variant="destructive">{partner.status}</Badge>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" disabled={loading} data-testid={`partner-actions-${partner.id}`}>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <MoreVertical className="w-4 h-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isPending && (
                <>
                  <DropdownMenuItem onClick={onCreateOnboardingLink}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Stripe Setup
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onResendInvite}>
                    <Mail className="w-4 h-4 mr-2" />
                    Resend Invite
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {stripeAccountId && (
                <>
                  <DropdownMenuItem onClick={onCheckStatus}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Check Stripe Status
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a 
                      href={`https://dashboard.stripe.com/connect/accounts/${stripeAccountId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View in Stripe
                    </a>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={onEdit}>
                <Percent className="w-4 h-4 mr-2" />
                Edit Details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onRemove} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Partner
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};


// Add Partner Modal with 2FA OTP verification
const AddPartnerModal = ({ open, onClose, organizationId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'otp' | 'success'
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    partnerType: 'revenue_partner',
    defaultSplitPercentage: 10,
    serviceIds: []
  });
  
  // Always fetch services so users can select them
  const { data: servicesData } = useSWR(
    organizationId ? `/organizations/${organizationId}/services` : null,
    fetcher
  );
  
  const services = servicesData?.services || [];
  
  // Check if all services are selected
  const allServicesSelected = services.length > 0 && formData.serviceIds.length === services.length;
  
  const handleSelectAllServices = () => {
    if (allServicesSelected) {
      setFormData({ ...formData, serviceIds: [] });
    } else {
      setFormData({ ...formData, serviceIds: services.map(s => s.id) });
    }
  };
  
  // Step 1: Submit form and request OTP
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setOtpSending(true);
    setOtpError('');
    
    try {
      // Request OTP for partner creation verification
      await apiV2.post(`/organizations/${organizationId}/partners/request-otp`, {
        name: formData.name,
        email: formData.email,
        partnerType: formData.partnerType,
        defaultSplitPercentage: formData.defaultSplitPercentage
      });
      
      toast.success('Verification code sent to your email');
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setOtpSending(false);
    }
  };
  
  // Step 2: Verify OTP and create partner
  const handleVerifyAndCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOtpError('');
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        partnerType: formData.partnerType,
        defaultSplitPercentage: formData.defaultSplitPercentage,
        otpCode: otpCode
      };
      
      await apiV2.post(`/organizations/${organizationId}/partners`, payload);
      
      toast.success('Partner created! They will receive an invite to connect their Stripe account.');
      
      // Reset and close
      setStep('form');
      setOtpCode('');
      setFormData({
        name: '',
        email: '',
        partnerType: 'revenue_partner',
        defaultSplitPercentage: 10,
        serviceIds: []
      });
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Verification failed';
      setOtpError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  // Resend OTP
  const handleResendOtp = async () => {
    setOtpSending(true);
    try {
      await apiV2.post(`/organizations/${organizationId}/partners/request-otp`, {
        name: formData.name,
        email: formData.email,
        partnerType: formData.partnerType,
        defaultSplitPercentage: formData.defaultSplitPercentage
      });
      toast.success('New code sent!');
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setOtpSending(false);
    }
  };
  
  // Reset when modal closes
  const handleClose = () => {
    setStep('form');
    setOtpCode('');
    setOtpError('');
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>Add Partner</DialogTitle>
              <DialogDescription>
                Add a revenue partner or affiliate to automatically share earnings
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleRequestOtp} className="space-y-4">
              {/* Partner Type */}
              <div className="space-y-2">
                <Label>Partner Type</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, partnerType: 'revenue_partner' })}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      formData.partnerType === 'revenue_partner'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className="w-5 h-5 text-blue-600 mb-1" />
                    <p className="font-medium text-sm">Revenue Partner</p>
                    <p className="text-xs text-gray-500">Gets % of all/selected services</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, partnerType: 'affiliate' })}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      formData.partnerType === 'affiliate'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Tag className="w-5 h-5 text-purple-600 mb-1" />
                    <p className="font-medium text-sm">Affiliate</p>
                    <p className="text-xs text-gray-500">Gets % when their code is used</p>
                  </button>
                </div>
              </div>
              
              {/* Name & Email - Stack on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    required
                    data-testid="partner-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    data-testid="partner-email-input"
                  />
                </div>
              </div>
              
              {/* Percentage */}
              <div className="space-y-2">
                <Label>Revenue Share Percentage <span className="text-red-500">*</span></Label>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <Input
                    type="number"
                    min="0.1"
                    max="99.9"
                    step="0.1"
                    value={formData.defaultSplitPercentage}
                    onChange={(e) => setFormData({ ...formData, defaultSplitPercentage: parseFloat(e.target.value) || 0 })}
                    className="w-20 sm:w-24"
                    required
                    data-testid="partner-percentage-input"
                  />
                  <span className="text-gray-500">%</span>
                  <span className="text-xs sm:text-sm text-gray-500">
                    (Partner receives this % of each sale)
                  </span>
                </div>
              </div>
              
              {/* Service Selection - Checkbox Style */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Applies To Services</Label>
                  {services.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectAllServices}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {allServicesSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
                
                {services.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Loading services...</p>
                ) : (
                  <div className="max-h-32 sm:max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 bg-gray-50">
                    {services.map((service) => (
                      <label 
                        key={service.id} 
                        className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.serviceIds.includes(service.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, serviceIds: [...formData.serviceIds, service.id] });
                            } else {
                              setFormData({ ...formData, serviceIds: formData.serviceIds.filter(id => id !== service.id) });
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{service.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  {formData.serviceIds.length === 0 
                    ? 'No services selected - partner will apply to ALL services by default'
                    : `${formData.serviceIds.length} service${formData.serviceIds.length > 1 ? 's' : ''} selected`}
                </p>
              </div>
              
              {/* Security Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm">
                  <p className="text-blue-800 font-medium">Verification Required</p>
                  <p className="text-blue-700">A verification code will be sent to your email to confirm this revenue sharing arrangement.</p>
                </div>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={handleClose} className="h-10 w-full sm:w-auto">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={otpSending || !formData.name || !formData.email} 
                  className="bg-blue-600 hover:bg-blue-700 text-white h-10 w-full sm:w-auto"
                >
                  {otpSending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Continue & Verify
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                Verify Partner Creation
              </DialogTitle>
              <DialogDescription>
                Enter the 6-digit code sent to your email to confirm adding this partner
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleVerifyAndCreate} className="space-y-4">
              {/* Partner Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Partner Name:</span>
                  <span className="font-medium truncate ml-2">{formData.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Partner Email:</span>
                  <span className="font-medium truncate ml-2">{formData.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Revenue Share:</span>
                  <span className="font-medium text-green-600">{formData.defaultSplitPercentage}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium capitalize">{formData.partnerType.replace('_', ' ')}</span>
                </div>
              </div>
              
              {/* OTP Input */}
              <div className="space-y-2">
                <Label>Verification Code</Label>
                <Input
                  type="text"
                  value={otpCode}
                  onChange={(e) => {
                    setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setOtpError('');
                  }}
                  placeholder="Enter 6-digit code"
                  className={`text-center text-xl sm:text-2xl tracking-widest font-mono ${otpError ? 'border-red-500' : ''}`}
                  maxLength={6}
                  autoFocus
                  required
                  data-testid="partner-otp-input"
                />
                {otpError && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <XCircle className="w-4 h-4" />
                    {otpError}
                  </p>
                )}
              </div>
              
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={otpSending}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                  data-testid="resend-otp-btn"
                >
                  {otpSending ? 'Sending...' : "Didn't receive code? Resend"}
                </button>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={() => setStep('form')} className="h-10 w-full sm:w-auto">
                  Back
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || otpCode.length !== 6} 
                  className="bg-blue-600 hover:bg-blue-700 text-white h-10 w-full sm:w-auto"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Verify & Create
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};


// Edit Partner Modal
const EditPartnerModal = ({ open, onClose, partner, organizationId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const partnerType = partner?.partner_type || partner?.partnerType || 'revenue_partner';
  
  // Fetch services for selection
  const { data: servicesData } = useSWR(
    organizationId ? `/organizations/${organizationId}/services` : null,
    fetcher
  );
  
  const services = servicesData?.services || [];
  
  // Initialize form data with proper service selection
  const applyToAll = partner?.apply_to_all_services !== false;
  const existingServiceIds = partner?.service_ids || partner?.assignedServices || [];
  
  const [formData, setFormData] = useState({
    name: partner?.name || '',
    percentage: partner?.percentage || partner?.defaultSplitPercentage || 10,
    service_ids: [],  // Will be set in useEffect
    discount_code: partner?.discount_code || partner?.promoCode || ''
  });
  
  // Set service_ids when services load
  React.useEffect(() => {
    if (services.length > 0) {
      if (applyToAll) {
        // If apply_to_all is true, select ALL services
        setFormData(prev => ({ ...prev, service_ids: services.map(s => s.id) }));
      } else {
        // Otherwise use the existing service_ids
        setFormData(prev => ({ ...prev, service_ids: existingServiceIds }));
      }
    }
  }, [services, applyToAll, existingServiceIds.length]);
  
  // Check if all services are selected
  const allServicesSelected = services.length > 0 && formData.service_ids.length === services.length;
  
  const handleSelectAllServices = () => {
    if (allServicesSelected) {
      setFormData({ ...formData, service_ids: [] });
    } else {
      setFormData({ ...formData, service_ids: services.map(s => s.id) });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await apiV2.patch(`/organizations/${organizationId}/partners/${partner.id}`, {
        name: formData.name,
        percentage: formData.percentage,
        apply_to_all_services: formData.service_ids.length === 0 || formData.service_ids.length === services.length,
        service_ids: formData.service_ids.length === services.length ? [] : formData.service_ids,
        discount_code: formData.discount_code || null
      });
      toast.success('Partner updated');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update partner');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Partner</DialogTitle>
          <DialogDescription>
            Update {partner?.name}&apos;s partnership details
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Partner Type - Read only */}
          <div className="space-y-2">
            <Label>Partner Type</Label>
            <div className="flex flex-wrap items-center gap-2">
              {partnerType === 'affiliate' ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                  <Tag className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Affiliate</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Revenue Partner</span>
                </div>
              )}
              <span className="text-xs text-gray-500">(cannot be changed)</span>
            </div>
          </div>
          
          {/* Name & Email - Stack on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="edit-partner-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={partner?.email || ''}
                disabled
                className="bg-gray-50 text-gray-500"
              />
            </div>
          </div>
          
          {/* Percentage */}
          <div className="space-y-2">
            <Label>Revenue Share Percentage <span className="text-red-500">*</span></Label>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Input
                type="number"
                min="0.1"
                max="99.9"
                step="0.1"
                value={formData.percentage}
                onChange={(e) => setFormData({ ...formData, percentage: parseFloat(e.target.value) })}
                className="w-20 sm:w-24"
                required
                data-testid="edit-partner-percentage"
              />
              <span className="text-gray-500">%</span>
              <span className="text-xs sm:text-sm text-gray-500">
                (Partner receives this % of each sale)
              </span>
            </div>
          </div>
          
          {/* Discount Code - For affiliates */}
          {partnerType === 'affiliate' && (
            <div className="space-y-2">
              <Label>Discount Code</Label>
              <Input
                value={formData.discount_code}
                onChange={(e) => setFormData({ ...formData, discount_code: e.target.value.toUpperCase() })}
                placeholder="e.g., PARTNER20"
                data-testid="edit-partner-discount-code"
              />
              <p className="text-xs text-gray-500">
                Customers using this code will be attributed to this affiliate
              </p>
            </div>
          )}
          
          {/* Service Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Applies To Services</Label>
              {services.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAllServices}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {allServicesSelected ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            
            {services.length === 0 ? (
              <p className="text-sm text-gray-500 italic">Loading services...</p>
            ) : (
              <div className="max-h-32 sm:max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1 bg-gray-50">
                {services.map((service) => (
                  <label 
                    key={service.id} 
                    className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={formData.service_ids.includes(service.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, service_ids: [...formData.service_ids, service.id] });
                        } else {
                          setFormData({ ...formData, service_ids: formData.service_ids.filter(id => id !== service.id) });
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{service.name}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500">
              {formData.service_ids.length === 0 
                ? 'No services selected - partner will apply to ALL services by default'
                : `${formData.service_ids.length} service${formData.service_ids.length > 1 ? 's' : ''} selected`}
            </p>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="h-10 w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className="bg-blue-600 hover:bg-blue-700 text-white h-10 w-full sm:w-auto"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartnersTab;
